import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/image-compress";

import {
  getMySalon,
  saveSalonProfile,
  saveOpeningHours,
  scrapeSalonWebsite,
  importWebsiteImages,
  registerUploadedImage,
  removeSalonImage,
  completeOnboarding,
} from "@/lib/salon.functions";
import { createCoinCheckoutSession } from "@/lib/payments.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MIN_COINS,
  COIN_PRICE_CENTS,
  DAILY_BUDGET_EUR,
  WEEKDAYS,
  formatEuro,
  grossFromNet,
  type OpeningHourRow,
} from "@/lib/salonturbo";
import { Loader2, Check, Sparkles, Coins, Clock, ImagePlus, Trash2 } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";

export const Route = createFileRoute("/_authenticated/onboarding")({
  head: () => ({
    meta: [
      { title: "Onboarding – SalonTurbo" },
      {
        name: "description",
        content: "Richte deinen Salon in wenigen Schritten ein und lade Boost-Coins auf.",
      },
      { property: "og:title", content: "Onboarding – SalonTurbo" },
      { property: "og:description", content: "Salon einrichten, Coins laden, Tage boosten." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: OnboardingPage,
});

const STEPS = ["So funktioniert's", "Salon-Daten", "Öffnungszeiten", "Bilder", "Coins"];

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fetchSalon = useServerFn(getMySalon);
  const { data, isLoading } = useQuery({ queryKey: ["my-salon"], queryFn: () => fetchSalon() });
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (data?.salon?.onboarding_completed_at) navigate({ to: "/dashboard" });
  }, [data, navigate]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["my-salon"] });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <div className="mb-8 flex items-center">
        <Wordmark />
      </div>
      <p className="eyebrow">
        Schritt {step + 1} von {STEPS.length}
      </p>
      <h1 className="mt-2 font-display text-3xl tracking-tight">{STEPS[step]}</h1>

      <Progress value={((step + 1) / STEPS.length) * 100} className="mt-4" />

      <div className="mt-8">
        {step === 0 && <IntroStep onNext={() => setStep(1)} />}
        {step === 1 && (
          <ProfileStep
            salon={data?.salon ?? null}
            onDone={async () => {
              await invalidate();
              setStep(2);
            }}
          />
        )}
        {step === 2 && (
          <HoursStep
            hours={data?.hours ?? []}
            onDone={async () => {
              await invalidate();
              setStep(3);
            }}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <ImagesStep
            websiteUrl={data?.salon?.website_url ?? null}
            images={data?.images ?? []}
            salonId={data?.salon?.id ?? null}
            onChanged={invalidate}
            onDone={() => setStep(4)}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && <CoinsStep salonId={data?.salon?.id ?? null} onBack={() => setStep(3)} />}
      </div>
    </main>
  );
}

function IntroStep({ onNext }: { onNext: () => void }) {
  const items = [
    {
      icon: Sparkles,
      title: "Deine Salon-Seite",
      text: "Wir bauen dir eine schlanke Salon-Seite, optimiert für schnelle Terminierung. ",
    },
    {
      icon: Coins,
      title: "Coins statt Ads-Chaos",
      text: "1 Coin = 25,00 € netto (zzgl. MwSt.) = 1 Boost-Tag",
    },
    {
      icon: Clock,
      title: "Tage einfach anklicken",
      text: "Du wählst bis zu 30 Tage im Voraus die Tage aus, an denen du Auslastung brauchst. Die Ads laufen automatisch an dem Tag und stoppen 2 Stunden vor Ladenschluss.",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-2xl">Freie Stühle füllen – automatisch und ohne teure Agenturkosten</CardTitle>
        <CardDescription>
          Wir übernehmen Kampagnen-Setup, Keywords, Zeitplanung und Landingpage. Du klickst nur die
          Tage an, an welchen du noch freie Termine hast.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {items.map((item) => (
          <div key={item.title} className="flex gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <item.icon className="size-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">{item.title}</h3>
              <p className="text-sm text-muted-foreground">{item.text}</p>
            </div>
          </div>
        ))}
        <Button size="lg" onClick={onNext}>
          Los geht's
        </Button>
      </CardContent>
    </Card>
  );
}

type SalonData = NonNullable<Awaited<ReturnType<typeof getMySalon>>["salon"]>;

type ProfileValues = {
  name: string;
  phone: string;
  street: string;
  postal_code: string;
  city: string;
  website_url: string | null;
  booking_url: string | null;
  description: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  tiktok_url: string | null;
  onboarding_step: number;
};

function ProfileStep({ salon, onDone }: { salon: SalonData | null; onDone: () => void }) {
  const save = useServerFn(saveSalonProfile);
  const mutation = useMutation({
    mutationFn: (values: ProfileValues) => save({ data: values }),
    onSuccess: onDone,
    onError: () => toast.error("Bitte prüfe deine Eingaben."),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Salon-Daten</CardTitle>
        <CardDescription>Diese Daten erscheinen auf deiner Salon-Seite.</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const value = (key: string) => String(form.get(key) ?? "").trim();
            mutation.mutate({
              name: value("name"),
              phone: value("phone"),
              street: value("street"),
              postal_code: value("postal_code"),
              city: value("city"),
              website_url: value("website_url") || null,
              booking_url: value("booking_url") || null,
              description: value("description") || null,
              instagram_url: value("instagram_url") || null,
              facebook_url: value("facebook_url") || null,
              tiktok_url: value("tiktok_url") || null,
              onboarding_step: 1,
            });
          }}
        >
          <Field name="name" label="Salonname *" defaultValue={salon?.name} required maxLength={120} />
          <Field
            name="phone"
            label="Telefonnummer des Salons *"
            defaultValue={salon?.phone ?? ""}
            required
            maxLength={40}
            placeholder="+49 30 1234567"
          />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field name="street" label="Straße & Nr. *" defaultValue={salon?.street ?? ""} required />
            <div className="grid grid-cols-[100px_1fr] gap-3">
              <Field name="postal_code" label="PLZ *" defaultValue={salon?.postal_code ?? ""} required />
              <Field name="city" label="Stadt *" defaultValue={salon?.city ?? ""} required />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              name="website_url"
              label="Website"
              defaultValue={salon?.website_url ?? ""}
              placeholder="www.mein-salon.de"
            />
            <Field
              name="booking_url"
              label="Online-Buchungslink (falls vorhanden)"
              defaultValue={salon?.booking_url ?? ""}
              placeholder="Treatwell, Shore, …"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Kurzbeschreibung</Label>
            <Textarea
              id="description"
              name="description"
              maxLength={600}
              defaultValue={salon?.description ?? ""}
              placeholder="Was macht deinen Salon besonders?"
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field name="instagram_url" label="Instagram" defaultValue={salon?.instagram_url ?? ""} />
            <Field name="facebook_url" label="Facebook" defaultValue={salon?.facebook_url ?? ""} />
            <Field name="tiktok_url" label="TikTok" defaultValue={salon?.tiktok_url ?? ""} />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Weiter
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({
  name,
  label,
  defaultValue,
  required,
  placeholder,
  maxLength = 200,
}: {
  name: string;
  label: string;
  defaultValue?: string | undefined;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={name}>{label}</Label>
      <Input
        id={name}
        name={name}
        defaultValue={defaultValue ?? ""}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
      />
    </div>
  );
}

function HoursStep({
  hours,
  onDone,
  onBack,
}: {
  hours: OpeningHourRow[];
  onDone: () => void;
  onBack: () => void;
}) {
  const save = useServerFn(saveOpeningHours);
  const [rows, setRows] = useState<OpeningHourRow[]>(() =>
    WEEKDAYS.map((day) => {
      const existing = hours.find((row) => row.weekday === day.value);
      return {
        weekday: day.value,
        is_closed: existing?.is_closed ?? day.value === 0,
        open_time: existing?.open_time?.slice(0, 5) ?? "09:00",
        close_time: existing?.close_time?.slice(0, 5) ?? "18:00",
      };
    }),
  );

  const mutation = useMutation({
    mutationFn: () => save({ data: { hours: rows } }),
    onSuccess: onDone,
    onError: () => toast.error("Öffnungszeiten konnten nicht gespeichert werden."),
  });

  function update(weekday: number, patch: Partial<OpeningHourRow>) {
    setRows((current) =>
      current.map((row) => (row.weekday === weekday ? { ...row, ...patch } : row)),
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Öffnungszeiten</CardTitle>
        <CardDescription>
          Trage die regulären Öffnungszeiten deines Salons ein.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.map((row) => {
          const day = WEEKDAYS.find((item) => item.value === row.weekday)!;
          return (
            <div key={row.weekday} className="flex flex-wrap items-center gap-3 border-b pb-3">
              <span className="w-24 font-medium">{day.label}</span>
              <Switch
                checked={!row.is_closed}
                onCheckedChange={(checked) => update(row.weekday, { is_closed: !checked })}
              />
              {row.is_closed ? (
                <span className="text-sm text-muted-foreground">geschlossen</span>
              ) : (
              <>
                  <Input
                    type="time"
                    className="w-28"
                    value={row.open_time ?? ""}
                    onChange={(event) => update(row.weekday, { open_time: event.target.value })}
                  />
                  <span className="text-muted-foreground">bis</span>
                  <Input
                    type="time"
                    className="w-28"
                    value={row.close_time ?? ""}
                    onChange={(event) => update(row.weekday, { close_time: event.target.value })}
                  />
                </>
              )}
            </div>
          );
        })}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Zurück
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Weiter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

type SalonImage = { id: string; url: string; path: string };

function ImagesStep({
  websiteUrl,
  images,
  salonId,
  onChanged,
  onDone,
  onBack,
}: {
  websiteUrl: string | null;
  images: SalonImage[];
  salonId: string | null;
  onChanged: () => Promise<unknown> | void;
  onDone: () => void;
  onBack: () => void;
}) {
  const scrape = useServerFn(scrapeSalonWebsite);
  const importImages = useServerFn(importWebsiteImages);
  const registerUpload = useServerFn(registerUploadedImage);
  const removeImage = useServerFn(removeSalonImage);
  const [selected, setSelected] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const suggestions = useQuery({
    queryKey: ["scrape", websiteUrl],
    enabled: Boolean(websiteUrl),
    queryFn: () => scrape({ data: { url: websiteUrl! } }),
  });

  const importMutation = useMutation({
    mutationFn: () => importImages({ data: { urls: selected } }),
    onSuccess: async (result) => {
      setSelected([]);
      await onChanged();
      if (result.failed.length) toast.warning(`${result.failed.length} Bilder konnten nicht geladen werden.`);
      else toast.success("Bilder übernommen.");
    },
    onError: () => toast.error("Import fehlgeschlagen."),
  });

  async function handleUpload(files: FileList | null) {
    if (!files?.length || !salonId) return;
    setUploading(true);
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    for (const file of Array.from(files).slice(0, 6)) {
      if (!file.type.startsWith("image/") || file.size > 25_000_000) {
        toast.error(`${file.name}: nur Bilder bis 25 MB.`);
        continue;
      }
      // Vor dem Upload verkleinern, damit die Salon-Seite schnell bleibt.
      const { blob, extension } = await compressImage(file);
      const path = `${userId}/${salonId}/${crypto.randomUUID()}.${extension}`;
      const { error } = await supabase.storage
        .from("salon-images")
        .upload(path, blob, { contentType: blob.type || file.type });

      if (error) {
        toast.error(`${file.name}: Upload fehlgeschlagen.`);
        continue;
      }
      await registerUpload({ data: { path } });
    }
    setUploading(false);
    await onChanged();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bilder für deine Salon-Seite</CardTitle>
        <CardDescription>
          {websiteUrl
            ? "Wähle Bilder von deiner Website aus oder lade eigene hoch."
            : "Lade Bilder deines Salons hoch."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {websiteUrl && (
          <div>
            <h3 className="mb-3 text-sm font-medium">Vorschläge von {websiteUrl}</h3>
            {suggestions.isLoading && (
              <p className="text-sm text-muted-foreground">
                <Loader2 className="mr-2 inline size-4 animate-spin" />
                Website wird analysiert…
              </p>
            )}
            {suggestions.data && suggestions.data.images.length === 0 && (
              <p className="text-sm text-muted-foreground">Keine passenden Bilder gefunden.</p>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(suggestions.data?.images ?? []).map((url) => {
                const active = selected.includes(url);
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() =>
                      setSelected((current) =>
                        active ? current.filter((item) => item !== url) : [...current, url].slice(0, 8),
                      )
                    }
                    className={`relative overflow-hidden rounded-lg border-2 transition ${
                      active ? "border-primary" : "border-transparent hover:border-border"
                    }`}
                  >
                    <img src={url} alt="Vorschlag von der Website" className="h-24 w-full object-cover" loading="lazy" />
                    {active && (
                      <span className="absolute right-1 top-1 rounded-full bg-primary p-1 text-primary-foreground">
                        <Check className="size-3" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <Button
                className="mt-3"
                onClick={() => importMutation.mutate()}
                disabled={importMutation.isPending}
              >
                {importMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                {selected.length} Bilder übernehmen
              </Button>
            )}
          </div>
        )}

        <div>
          <h3 className="mb-3 text-sm font-medium">Eigene Bilder</h3>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed px-4 py-6 text-sm text-muted-foreground hover:bg-secondary/50">
            <ImagePlus className="size-4" />
            {uploading ? "Wird hochgeladen…" : "Bilder auswählen (JPG/PNG, max. 8 MB)"}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(event) => void handleUpload(event.target.files)}
            />
          </label>
        </div>

        {images.length > 0 && (
          <div>
            <h3 className="mb-3 text-sm font-medium">Ausgewählte Bilder ({images.length})</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {images.map((image) => (
                <div key={image.id} className="group relative overflow-hidden rounded-lg border">
                  <img src={image.url} alt="Salon" className="h-24 w-full object-cover" />
                  <button
                    type="button"
                    onClick={async () => {
                      await removeImage({ data: { id: image.id } });
                      await onChanged();
                    }}
                    className="absolute right-1 top-1 rounded-full bg-background/90 p-1 text-destructive"
                    aria-label="Bild entfernen"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Zurück
          </Button>
          <Button
            disabled={importMutation.isPending || uploading}
            onClick={async () => {
              if (selected.length > 0) {
                try {
                  await importMutation.mutateAsync();
                } catch {
                  return;
                }
              }
              onDone();
            }}
          >
            {importMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            Weiter zu den Coins
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CoinsStep({ salonId, onBack }: { salonId: string | null; onBack: () => void }) {
  const [coins, setCoins] = useState(MIN_COINS);
  const finish = useServerFn(completeOnboarding);
  const checkout = useServerFn(createCoinCheckoutSession);
  const total = useMemo(() => coins * COIN_PRICE_CENTS, [coins]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!salonId) throw new Error("no_salon");
      // Checkout zuerst: schlägt Stripe fehl, bleibt der Nutzer im Onboarding.
      const session = await checkout({ data: { salonId, coins, returnPath: "/dashboard" } });
      if (!session.url) throw new Error("no_checkout_url");
      await finish();
      return session;
    },
    onSuccess: (result) => {
      if (result.url) window.location.href = result.url;
    },
    onError: (error) => {
      toast.error(
        `Zahlung konnte nicht gestartet werden: ${error instanceof Error ? error.message : "unbekannter Fehler"}`,
      );
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Coins aufladen</CardTitle>
        <CardDescription>
          1 Coin = 1 Boost-Tag mit {DAILY_BUDGET_EUR} € Google-Budget = {formatEuro(COIN_PRICE_CENTS)}{" "}
          netto zzgl. MwSt. Minimum {MIN_COINS} Coins.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-center gap-6 rounded-xl bg-secondary/60 p-6">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCoins((value) => Math.max(MIN_COINS, value - 1))}
            aria-label="Weniger Coins"
          >
            –
          </Button>
          <div className="text-center">
            <p className="font-display text-4xl font-semibold">{coins}</p>
            <p className="text-sm text-muted-foreground">Coins</p>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setCoins((value) => Math.min(100, value + 1))}
            aria-label="Mehr Coins"
          >
            +
          </Button>
        </div>
        <div className="flex items-center justify-between text-lg">
          <span>Gesamt (netto)</span>
          <strong>{formatEuro(total)}</strong>
        </div>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>inkl. 19 % MwSt.</span>
          <span>{formatEuro(grossFromNet(total))}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Zahlung über Stripe. Der endgültige Steuersatz wird im Checkout anhand deiner
          Rechnungsadresse ermittelt (USt-IdNr. für Reverse Charge kann dort angegeben werden).
          Rechnungen findest du danach jederzeit im Dashboard.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Zurück
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !salonId}>
            {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {formatEuro(total)} bezahlen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
